import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import PencilEditIcon from '@/assets/icons/pencil-edit.svg';
import { ExternalLinkIcon } from '@/components/icons/external-link';
import { Button } from '@/components/ui/button';
import { FormSelect } from '@/components/ui/select';
import { Typography } from '@/components/ui/typography';
import { useAuthenticatedContext } from '@/contexts/auth-context';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { MessageModal } from './message-modal';
import { MessagePreview } from './message-preview';
interface AudienceMessageFormProps {
  openingId: string;
}

export function AudienceMessageForm({ openingId }: AudienceMessageFormProps) {
  const intl = useIntl();
  const {
    whoami: { wx_url: wxUrl },
  } = useAuthenticatedContext();
  const { hookForm } = useJobPostingContext();
  const {
    control,
    watch,
    formState: { errors },
  } = hookForm;

  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);

  const message = watch(
    `channels.pool.poolDetails.openingMessages.${openingId}.body`,
  );
  const messageError =
    errors?.channels?.pool?.poolDetails?.openingMessages?.[openingId]?.body
      ?.message ??
    errors?.channels?.pool?.poolDetails?.openingMessages?.[openingId]?.subject
      ?.message;

  const {
    data: audienceOptions,
    isLoading: isLoadingAudienceOptions,
    refetch: refetchAudienceOptions,
  } = useQuery({
    queryKey: queryKeys.jobPosting.audienceOptions,
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingPurchasesPoolAudiences(),
  });

  const handleEditMessage = () => {
    setIsMessageModalOpen(true);
  };

  const handleCreateAudience = () => {
    if (!wxUrl) return;
    window.open(`${wxUrl}/pool/talent`, '_blank');
  };

  const handleFormSelectOpenChange = (open: boolean) => {
    if (open) void refetchAudienceOptions();
  };

  return (
    <>
      <div className="flex gap-4 px-4">
        {/* Audience selection */}
        <FormSelect
          name={`channels.pool.poolDetails.openingMessages.${openingId}.audienceId`}
          control={control}
          options={audienceOptions ?? []}
          placeholder={intl.formatMessage({
            defaultMessage: 'Select audience',
            description: 'Placeholder for audience selection',
          })}
          label={intl.formatMessage({
            defaultMessage: 'To (audience)',
            description: 'Label for audience selection',
          })}
          description={intl.formatMessage({
            defaultMessage:
              'Select the audience you’ll be sending your campaign to',
            description: 'Description for audience selection input',
          })}
          isMultiSelect={false}
          isLoading={isLoadingAudienceOptions}
          onOpenChange={handleFormSelectOpenChange}
          error={
            errors?.channels?.pool?.poolDetails?.openingMessages?.[openingId]
              ?.audienceId?.message
          }
          required
        />

        {/* Create audience button */}
        <Button
          type="button"
          variant="secondary"
          className="self-end"
          disabled={!wxUrl}
          onClick={handleCreateAudience}
        >
          <ExternalLinkIcon className="text-fx-primary" />
          <FormattedMessage
            defaultMessage="Create audience"
            description="Create audience button"
          />
        </Button>
      </div>

      <div className="flex w-full flex-col gap-2 px-4">
        <div className="flex justify-between gap-4">
          <Typography variant="subtitle2">
            <FormattedMessage
              defaultMessage="Campaign message"
              description="Label for campaign message creation"
            />
            <span className="text-fx-rose">*</span>
          </Typography>
          {/* Edit message button (if message is already written) */}
          {message && (
            <Button
              type="button"
              variant="ghost"
              className="h-7 self-start"
              onClick={handleEditMessage}
            >
              <img src={PencilEditIcon} alt="Edit" />
              <FormattedMessage
                defaultMessage="Edit message"
                description="Write message button"
              />
            </Button>
          )}
        </div>

        {message ? (
          <MessagePreview
            openingId={openingId}
            onViewMore={handleEditMessage}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="secondary"
              className="py-2.5"
              onClick={handleEditMessage}
            >
              <img src={PencilEditIcon} alt="Write" />
              <FormattedMessage
                defaultMessage="Write message"
                description="Write message button"
              />
            </Button>
            {!!messageError && (
              <Typography variant="small1" className="!text-fx-rose">
                {messageError}
              </Typography>
            )}
          </div>
        )}
      </div>

      <MessageModal
        open={isMessageModalOpen}
        onOpenChange={setIsMessageModalOpen}
        openingId={openingId}
      />
    </>
  );
}
